import { useState } from 'react'
import { Button, Modal } from '@tractian/ui'

import {
  getFeatureFlagOverrides,
  setFeatureFlagOverrides,
  type FeatureFlagOverride,
  type ForcedFeatureFlagOverrides
} from '@/features/feature-flags/domain/feature-flag-overrides'
import { FEATURE_FLAGS, type FeatureFlag } from '@/features/feature-flags/domain/feature-flags'

export type FeatureFlagsToolProps = {
  /** Returns to the dev-tools list. */
  onBack: () => void
  /** Called after Save applies the staged overrides — closes the modal. */
  onClose: () => void
}

const OVERRIDE_OPTIONS: ReadonlyArray<{ value: FeatureFlagOverride; label: string }> = [
  { value: 'false', label: 'False' },
  { value: 'unchanged', label: 'Unchanged' },
  { value: 'true', label: 'True' }
]

const ACTIVE_STYLES: Record<FeatureFlagOverride, string> = {
  false: 'border-red-500 bg-red-500 text-white',
  unchanged: 'border-gray-400 bg-gray-400 text-white',
  true: 'border-green-500 bg-green-500 text-white'
}

function overridesEqual(a: ForcedFeatureFlagOverrides, b: ForcedFeatureFlagOverrides) {
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  return (
    aKeys.length === bKeys.length &&
    aKeys.every(key => a[key as FeatureFlag] === b[key as FeatureFlag])
  )
}

function withOverride(
  overrides: ForcedFeatureFlagOverrides,
  flag: FeatureFlag,
  override: FeatureFlagOverride
): ForcedFeatureFlagOverrides {
  const next = { ...overrides }
  if (override === 'unchanged') {
    delete next[flag]
  } else {
    next[flag] = override
  }
  return next
}

// Edits are staged in a local draft: nothing reaches the store (or the rest of
// the app) until Save. Revert discards the draft back to the persisted state;
// Set all to Unchanged stages a full clear. The list scrolls in the modal body
// while the actions stay pinned in the modal footer.
export function FeatureFlagsTool({ onBack, onClose }: FeatureFlagsToolProps) {
  const [search, setSearch] = useState('')
  const [draft, setDraft] = useState<ForcedFeatureFlagOverrides>(() => getFeatureFlagOverrides())

  const persisted = getFeatureFlagOverrides()
  const dirty = !overridesEqual(draft, persisted)
  const normalizedSearch = search.trim().toLowerCase()
  const flags = FEATURE_FLAGS.filter(flag => flag.key.toLowerCase().includes(normalizedSearch))

  function save() {
    setFeatureFlagOverrides(draft)
    onClose()
  }

  return (
    <>
      <Modal.Body>
        <div className="flex flex-col gap-4">
          <input
            type="text"
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Search feature flags"
            aria-label="Search feature flags"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
          />

          <div className="flex flex-col gap-3">
            {flags.length === 0 && (
              <p className="text-sm text-gray-500">No feature flags match "{search}".</p>
            )}
            {flags.map(flag => {
              const value = draft[flag.key] ?? 'unchanged'
              return (
                <div key={flag.key} className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="m-0 truncate font-mono text-sm leading-tight" title={flag.key}>
                      {flag.key}
                    </p>
                    <p className="m-0 text-xs leading-tight text-gray-400">added {flag.addedAt}</p>
                  </div>
                  <div
                    className="flex shrink-0"
                    role="radiogroup"
                    aria-label={`${flag.key} override`}
                  >
                    {OVERRIDE_OPTIONS.map((option, index) => {
                      const active = value === option.value
                      return (
                        <button
                          key={option.value}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          onClick={() =>
                            setDraft(current => withOverride(current, flag.key, option.value))
                          }
                          className={[
                            'border px-3 py-1.5 text-sm',
                            index === 0 ? 'rounded-l-md' : '-ml-px',
                            index === OVERRIDE_OPTIONS.length - 1 ? 'rounded-r-md' : '',
                            active
                              ? ACTIVE_STYLES[option.value]
                              : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                          ].join(' ')}
                        >
                          {option.label}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button type="button" variant="outline" onClick={onBack}>
          ← All tools
        </Button>
        <Modal.FooterActions>
          <Button type="button" variant="outline" onClick={() => setDraft({})}>
            Set all to Unchanged
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={!dirty}
            onClick={() => setDraft(getFeatureFlagOverrides())}
          >
            Revert Changes
          </Button>
          <Button type="button" disabled={!dirty} onClick={save}>
            Save
          </Button>
        </Modal.FooterActions>
      </Modal.Footer>
    </>
  )
}
